import {
  ClipboardList,
  Clock,
  Download,
  GraduationCap,
  History,
  ListOrdered,
  Plus,
  Settings2,
  Star,
  Trash2,
  Trophy,
  UserCheck,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import {
  AcademicPanelCard,
  AcademicPanelListItem,
  AcademicPanelStatBanner,
  AcademicPanelTableWrap,
} from './AcademicPanelCard';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import AcademiaSelect from '../../common/AcademiaSelect';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import AdminPageShell from '../AdminPageShell';
import { isAdministrateur } from '../../../utils/adminAccess';
import AdminPagination from '../AdminPagination';
import AdminToast from '../AdminToast';
import {
  createAcademicVacation,
  deleteAcademicNote,
  deleteAcademicVacation,
  downloadDeliberationPdf,
  fetchAcademicConfiguration,
  fetchAcademicSessions,
  fetchAcademicVacations,
  fetchAdminCohortes,
  fetchAttendanceSheet,
  fetchAttendanceSheetRegistry,
  attendanceSheetExportPdfPath,
  fetchCohorteClassement,
  fetchCohorteDeliberations,
  fetchCohorteNotes,
  fetchEvaluationsFormateurAdmin,
  fetchSoutenancePlanning,
  patchAcademicConfiguration,
  patchNoteStatutDepot,
  planifierSoutenanceAdmin,
  previewCohorteClassement,
  runCohorteDeliberation,
  saveAcademicNote,
  saveAttendanceSheet,
  uploadAdminFormateurFile,
} from '../../../services/academiaAdminService';
import { downloadAuthenticatedFile } from '../../../services/api';

type PresenceRegistryRow = {
  dateSeance: string;
  presencesCount: number;
  archivee: boolean;
  dateArchivage: string | null;
};

function attendanceDayKey(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toISOString().slice(0, 10);
}

function formatRegistryDate(iso: string) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

type TabId = 'presences' | 'notes' | 'soutenances' | 'deliberation' | 'evaluations' | 'vacations' | 'config';

const STATUT_DEPOT_OPTIONS = [
  { value: 'DEPOSE', label: 'Déposé' },
  { value: 'EN_CORRECTION', label: 'En correction' },
  { value: 'VALIDE', label: 'Validé' },
  { value: 'REFUSE', label: 'Refusé' },
] as const;

const PRATIQUE_TYPES = new Set(['TP', 'SOUTENANCE', 'PROJET']);

const PRESENCE_STATUTS = ['PRESENT', 'ABSENT', 'RETARD', 'EXCUSE'] as const;

const ACADEMIC_FIELD_LABEL = 'text-xs font-bold text-on-surface-variant';
const ACADEMIC_FIELD_INPUT = 'academia-field mt-1 w-full text-sm text-slate-900';
const BTN_PRIMARY = 'academic-panel-btn academic-panel-btn--primary';
const BTN_SECONDARY = 'academic-panel-btn academic-panel-btn--secondary';
const BTN_DANGER = 'academic-panel-btn academic-panel-btn--danger';
const TABLE_CLASS = 'academic-panel-table';

const EVAL_TYPES = [
  { value: 'INTERROGATION', label: 'Interrogation' },
  { value: 'DEVOIR', label: 'Devoir' },
  { value: 'TP', label: 'Travaux pratiques' },
  { value: 'EXAMEN', label: 'Examen' },
  { value: 'SOUTENANCE', label: 'Soutenance' },
  { value: 'PROJET', label: 'Projet' },
];

function SoutenanceRowEditor({
  row,
  onSave,
}: {
  row: {
    idInscription: string;
    matricule: string;
    nomComplet: string;
    creneauSoutenance: string | null;
    statutDepot: string | null;
  };
  onSave: (creneauIso: string) => Promise<void>;
}) {
  const initial = row.creneauSoutenance
    ? String(row.creneauSoutenance).slice(0, 16)
    : '';
  const [creneau, setCreneau] = useState(initial);
  const [busy, setBusy] = useState(false);

  return (
    <tr>
      <td className="px-4 py-2">
        {row.nomComplet}
        <span className="ml-1 font-mono text-xs text-on-surface-variant">({row.matricule})</span>
      </td>
      <td className="px-4 py-2">
        <label className="block min-w-[12rem]">
          <span className={ACADEMIC_FIELD_LABEL}>Créneau de soutenance</span>
          <input
            type="datetime-local"
            className={ACADEMIC_FIELD_INPUT}
            value={creneau}
            onChange={(e) => setCreneau(e.target.value)}
          />
        </label>
      </td>
      <td className="px-4 py-2 text-xs">{row.statutDepot ?? '—'}</td>
      <td className="px-4 py-2 text-right">
        <button
          type="button"
          disabled={!creneau || busy}
          className={`${BTN_PRIMARY} !px-3 !py-1.5 text-xs disabled:opacity-50`}
          onClick={async () => {
            setBusy(true);
            try {
              await onSave(new Date(creneau).toISOString());
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? '…' : 'Planifier'}
        </button>
      </td>
    </tr>
  );
}

const TABS: { id: TabId; label: string }[] = [
  { id: 'presences', label: 'Présences' },
  { id: 'notes', label: 'Notes & évaluations' },
  { id: 'soutenances', label: 'Soutenances' },
  { id: 'deliberation', label: 'Délibérations & classement' },
  { id: 'evaluations', label: 'Éval. formateurs' },
  { id: 'vacations', label: 'Vacations' },
  { id: 'config', label: 'Pondérations' },
];

export default function AcademiaAcademicPanel({ user }: { user?: { role?: string } | null }) {
  const canDelete = isAdministrateur(user);
  const [tab, setTab] = useState<TabId>('presences');
  const [cohortes, setCohortes] = useState<{ id: string; code?: string; libelle?: string }[]>([]);
  const [cohorteId, setCohorteId] = useState('');
  const [sessions, setSessions] = useState<{ id: string; jourSemaine?: string; vacation?: { libelle?: string }; salle?: { code?: string } }[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [dateSeance, setDateSeance] = useState(() => new Date().toISOString().slice(0, 10));
  const [attendance, setAttendance] = useState<{
    apprenants: { idInscription: string; nomComplet: string; matricule: string; statut: string | null }[];
    feuille?: { archivee: boolean; dateArchivage: string | null };
  } | null>(null);
  const [presenceRegistry, setPresenceRegistry] = useState<PresenceRegistryRow[]>([]);
  const [presenceListFilter, setPresenceListFilter] = useState<'en_cours' | 'archivees'>('en_cours');
  const [downloadingPresencePdf, setDownloadingPresencePdf] = useState(false);
  const [notesRows, setNotesRows] = useState<
    {
      idInscription: string;
      nomComplet: string;
      matricule: string;
      notes: {
        id: string;
        typeEvaluation?: string;
        typeLabel: string;
        libelle: string;
        noteSur20: number;
        statutDepot?: string | null;
        urlFichier?: string | null;
      }[];
      stats: { moyenneSur20: number | null };
    }[]
  >([]);
  const [vacations, setVacations] = useState<
    { id?: string; code: string; libelle: string; heureDebut: string; heureFin: string; ordre?: number }[]
  >([]);
  const [deliberations, setDeliberations] = useState<
    { id: string; dateDeliberation: string; resultats?: { periode?: string; synthese?: { admis: number; total: number } } }[]
  >([]);
  const [academicConfig, setAcademicConfig] = useState<{
    poidsAcademique: number;
    poidsPresence: number;
    poidsPratique: number;
    poidsDiscipline: number;
    poidsImplicationPedagogique: number;
    poidsProjets: number;
    seuilAdmissionSur20: number;
    tailleShortlist: number;
  } | null>(null);
  const [vacationForm, setVacationForm] = useState({
    code: '',
    libelle: '',
    heureDebut: '08:00',
    heureFin: '10:00',
    ordre: '0',
  });
  const [classement, setClassement] = useState<{ rang?: number; nomComplet: string; scoreGlobal: number }[]>([]);
  const [previewRows, setPreviewRows] = useState<
    { rang: number; nomComplet: string; scoreGlobal: number; admis: boolean }[]
  >([]);
  const [soutenanceRows, setSoutenanceRows] = useState<
    {
      idInscription: string;
      matricule: string;
      nomComplet: string;
      creneauSoutenance: string | null;
      statutDepot: string | null;
      noteId: string | null;
    }[]
  >([]);
  const [evaluationsFormateur, setEvaluationsFormateur] = useState<
    {
      id: string;
      formateur: { nom: string };
      apprenant: { nom: string; matricule?: string };
      notePonctualite: number;
      notePedagogie: number;
      commentaire?: string;
      dateCreation: string;
    }[]
  >([]);
  const [periode, setPeriode] = useState(() => `${new Date().getFullYear()}-S1`);
  const [delibResult, setDelibResult] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [vacationToDelete, setVacationToDelete] = useState<{ id: string; libelle: string } | null>(null);
  const [noteToDelete, setNoteToDelete] = useState<{ id: string; libelle: string } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const vacationPagination = useAdminTablePagination(vacations);

  const pushSuccessToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3000);
  };

  const [noteForm, setNoteForm] = useState({
    idInscription: '',
    typeEvaluation: 'DEVOIR',
    libelle: '',
    noteSur20: '10',
    coefficient: '1',
    urlFichier: '',
    juryCommentaire: '',
    creneauSoutenance: '',
  });
  const [noteDepotFile, setNoteDepotFile] = useState<File | null>(null);
  const [savingNote, setSavingNote] = useState(false);

  const loadCohortes = useCallback(async () => {
    const rows = await fetchAdminCohortes();
    setCohortes(rows);
    if (!cohorteId && rows[0]?.id) setCohorteId(rows[0].id);
  }, [cohorteId]);

  useEffect(() => {
    void loadCohortes();
    void fetchAcademicVacations().then(setVacations);
  }, [loadCohortes]);

  useEffect(() => {
    if (!cohorteId) return;
    void fetchAcademicSessions(cohorteId).then((s) => {
      setSessions(s);
      if (s[0]?.id) setSessionId(s[0].id);
    });
  }, [cohorteId]);

  const loadAttendance = async () => {
    if (!sessionId) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchAttendanceSheet(sessionId, dateSeance);
      setAttendance(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur présences');
    } finally {
      setLoading(false);
    }
  };

  const loadPresenceRegistry = useCallback(async () => {
    if (!sessionId) {
      setPresenceRegistry([]);
      return;
    }
    try {
      const data = await fetchAttendanceSheetRegistry(sessionId);
      setPresenceRegistry(Array.isArray(data?.feuilles) ? data.feuilles : []);
    } catch {
      setPresenceRegistry([]);
    }
  }, [sessionId]);

  const loadNotes = async () => {
    if (!cohorteId) return;
    setLoading(true);
    setError('');
    try {
      setNotesRows(await fetchCohorteNotes(cohorteId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur notes');
    } finally {
      setLoading(false);
    }
  };

  const loadClassement = async () => {
    if (!cohorteId) return;
    try {
      const data = await fetchCohorteClassement(cohorteId, periode);
      setClassement(data.classement ?? []);
    } catch {
      setClassement([]);
    }
  };

  const loadDeliberations = async () => {
    if (!cohorteId) return;
    try {
      setDeliberations(await fetchCohorteDeliberations(cohorteId));
    } catch {
      setDeliberations([]);
    }
  };

  const loadSoutenances = async () => {
    if (!cohorteId) return;
    try {
      setSoutenanceRows(await fetchSoutenancePlanning(cohorteId));
    } catch {
      setSoutenanceRows([]);
    }
  };

  const loadEvaluationsFormateur = async () => {
    try {
      setEvaluationsFormateur(await fetchEvaluationsFormateurAdmin(cohorteId || undefined));
    } catch {
      setEvaluationsFormateur([]);
    }
  };

  useEffect(() => {
    if (tab === 'config') {
      void fetchAcademicConfiguration().then(setAcademicConfig);
    }
  }, [tab]);

  useEffect(() => {
    if (tab === 'presences' && sessionId) {
      void loadAttendance();
      void loadPresenceRegistry();
    }
    if (tab === 'notes' && cohorteId) void loadNotes();
    if (tab === 'soutenances' && cohorteId) void loadSoutenances();
    if (tab === 'evaluations') void loadEvaluationsFormateur();
    if (tab === 'deliberation' && cohorteId) {
      void loadClassement();
      void loadDeliberations();
    }
  }, [tab, sessionId, cohorteId, dateSeance]);

  useRegisterPortalRefresh(() => {
    void loadCohortes();
    void fetchAcademicVacations().then(setVacations);
    if (tab === 'config') void fetchAcademicConfiguration().then(setAcademicConfig);
    if (tab === 'presences' && sessionId) {
      void loadAttendance();
      void loadPresenceRegistry();
    }
    if (tab === 'notes' && cohorteId) void loadNotes();
    if (tab === 'soutenances' && cohorteId) void loadSoutenances();
    if (tab === 'evaluations') void loadEvaluationsFormateur();
    if (tab === 'deliberation' && cohorteId) {
      void loadClassement();
      void loadDeliberations();
    }
  });

  const renderCohorteSelect = (fullWidth = false) => (
    <label className={fullWidth ? 'block w-full min-w-0 text-sm' : 'block text-sm'}>
      <span className={ACADEMIC_FIELD_LABEL}>Cohorte</span>
      <AcademiaSelect
        className={fullWidth ? 'mt-1 w-full' : 'mt-1 max-w-md'}
        value={cohorteId}
        onChange={(e) => setCohorteId(e.target.value)}
      >
        <option value="">— Choisir —</option>
        {cohortes.map((c) => (
          <option key={c.id} value={c.id}>
            {c.code || c.libelle}
          </option>
        ))}
      </AcademiaSelect>
    </label>
  );

  const cohorteSelect = renderCohorteSelect(false);

  const noteCohorteSelect = (
    <div className="academic-panel-cohorte-command">
      <span className="academic-panel-cohorte-command__label">Cohorte — sélection principale</span>
      <p className="academic-panel-cohorte-command__hint">
        Détermine la liste des apprenants et les données affichées dans ce formulaire.
      </p>
      <AcademiaSelect
        className="w-full"
        value={cohorteId}
        onChange={(e) => setCohorteId(e.target.value)}
      >
        <option value="">— Choisir une cohorte —</option>
        {cohortes.map((c) => (
          <option key={c.id} value={c.id}>
            {c.code || c.libelle}
          </option>
        ))}
      </AcademiaSelect>
    </div>
  );

  const tabContent = useMemo(() => {
    if (tab === 'config' && academicConfig) {
      return (
        <AcademicPanelCard
          variant="accent"
          icon={<Settings2 className="h-5 w-5" />}
          title="Pondérations & classement"
          subtitle="Coefficients du score global (somme = 1). La shortlist alimente la page publique /excellence."
          footer={
            <button
              type="button"
              className={BTN_PRIMARY}
              onClick={async () => {
                try {
                  await patchAcademicConfiguration(academicConfig);
                  setToast('Configuration enregistrée');
                } catch (e) {
                  setError(e instanceof Error ? e.message : 'Échec');
                }
              }}
            >
              Enregistrer les pondérations
            </button>
          }
        >
          <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
            {(
              [
                ['poidsAcademique', 'Résultats académiques'],
                ['poidsPresence', 'Taux de présence'],
                ['poidsPratique', 'Performances pratiques'],
                ['poidsDiscipline', 'Discipline'],
                ['poidsImplicationPedagogique', 'Implication pédagogique'],
                ['poidsProjets', 'Projets réalisés'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>{label}</span>
                <input
                  type="number"
                  step={0.05}
                  min={0}
                  max={1}
                  className={ACADEMIC_FIELD_INPUT}
                  value={academicConfig[key]}
                  onChange={(e) =>
                    setAcademicConfig((c) => (c ? { ...c, [key]: Number(e.target.value) } : c))
                  }
                />
              </label>
            ))}
            <label className="block text-sm">
              <span className={ACADEMIC_FIELD_LABEL}>Seuil admission (/20)</span>
              <input
                type="number"
                className={ACADEMIC_FIELD_INPUT}
                value={academicConfig.seuilAdmissionSur20}
                onChange={(e) =>
                  setAcademicConfig((c) =>
                    c ? { ...c, seuilAdmissionSur20: Number(e.target.value) } : c,
                  )
                }
              />
            </label>
            <label className="block text-sm">
              <span className={ACADEMIC_FIELD_LABEL}>Taille shortlist publique</span>
              <input
                type="number"
                className={ACADEMIC_FIELD_INPUT}
                value={academicConfig.tailleShortlist}
                onChange={(e) =>
                  setAcademicConfig((c) => (c ? { ...c, tailleShortlist: Number(e.target.value) } : c))
                }
              />
            </label>
          </div>
        </AcademicPanelCard>
      );
    }

    if (tab === 'vacations') {
      return (
        <div className="space-y-5">
          <AcademicPanelCard
            variant="accent"
            icon={<Plus className="h-5 w-5" />}
            title="Nouvelle vacation"
            subtitle="Créneaux horaires officiels utilisés dans le planning des séances."
          >
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
              <label className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Code</span>
                <input
                  className={ACADEMIC_FIELD_INPUT}
                  value={vacationForm.code}
                  onChange={(e) => setVacationForm((f) => ({ ...f, code: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Libellé</span>
                <input
                  className={ACADEMIC_FIELD_INPUT}
                  value={vacationForm.libelle}
                  onChange={(e) => setVacationForm((f) => ({ ...f, libelle: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Heure de début</span>
                <input
                  type="time"
                  className={ACADEMIC_FIELD_INPUT}
                  value={vacationForm.heureDebut}
                  onChange={(e) => setVacationForm((f) => ({ ...f, heureDebut: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Heure de fin</span>
                <input
                  type="time"
                  className={ACADEMIC_FIELD_INPUT}
                  value={vacationForm.heureFin}
                  onChange={(e) => setVacationForm((f) => ({ ...f, heureFin: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Ordre d&apos;affichage</span>
                <input
                  type="number"
                  min={0}
                  className={ACADEMIC_FIELD_INPUT}
                  value={vacationForm.ordre}
                  onChange={(e) => setVacationForm((f) => ({ ...f, ordre: e.target.value }))}
                />
              </label>
              <div className="flex items-end">
              <button
                type="button"
                className={`${BTN_PRIMARY} w-full`}
                onClick={async () => {
                  try {
                    await createAcademicVacation({
                      code: vacationForm.code,
                      libelle: vacationForm.libelle,
                      heureDebut: vacationForm.heureDebut,
                      heureFin: vacationForm.heureFin,
                      ordre: Number(vacationForm.ordre),
                    });
                    setVacations(await fetchAcademicVacations());
                    pushSuccessToast('Vacation créée avec succès.');
                  } catch (e) {
                    setError(e instanceof Error ? e.message : 'Échec');
                  }
                }}
              >
                Ajouter
              </button>
              </div>
            </div>
          </AcademicPanelCard>
          <AcademicPanelCard
            icon={<Clock className="h-5 w-5" />}
            title="Vacations enregistrées"
            subtitle={`${vacations.length} créneau(x) configuré(s)`}
            badge={<span className="academic-panel-badge">{vacations.length}</span>}
            flush
            footer={
              <AdminPagination
                currentPage={vacationPagination.currentPage}
                totalPages={vacationPagination.totalPages}
                onPageChange={vacationPagination.onPageChange}
                summary={vacationPagination.summary}
              />
            }
          >
            <AcademicPanelTableWrap
              empty={
                vacationPagination.paginatedItems.length === 0 ? 'Aucune vacation.' : undefined
              }
            >
              <table className={TABLE_CLASS}>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Libellé</th>
                    <th>Horaire</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vacationPagination.paginatedItems.map((v) => (
                    <tr key={v.id ?? v.code}>
                      <td className="font-mono text-xs">{v.code}</td>
                      <td>{v.libelle}</td>
                      <td>
                        {v.heureDebut} – {v.heureFin}
                      </td>
                      <td className="text-right">
                        {v.id && canDelete ? (
                          <button
                            type="button"
                            className={`${BTN_DANGER} inline-flex items-center gap-1`}
                            onClick={() => setVacationToDelete({ id: v.id!, libelle: v.libelle })}
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                            Supprimer
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AcademicPanelTableWrap>
          </AcademicPanelCard>
        </div>
      );
    }

    if (tab === 'presences') {
      const archivedRegistry = presenceRegistry.filter((row) => row.archivee);
      const enCoursRegistry = presenceRegistry.filter((row) => !row.archivee && row.presencesCount > 0);
      const currentRegistryRow =
        presenceRegistry.find((row) => attendanceDayKey(row.dateSeance) === attendanceDayKey(dateSeance)) ??
        null;
      const presenceSheetArchived = attendance?.feuille?.archivee ?? false;
      const canDownloadPresencePdf = (currentRegistryRow?.presencesCount ?? 0) > 0;

      const openRegistryDate = (iso: string) => {
        setDateSeance(attendanceDayKey(iso));
      };

      const handleDownloadPresencePdf = async () => {
        if (!sessionId || !canDownloadPresencePdf) return;
        setDownloadingPresencePdf(true);
        try {
          await downloadAuthenticatedFile(
            attendanceSheetExportPdfPath(sessionId, dateSeance),
            `presences-${dateSeance.slice(0, 10)}.pdf`,
          );
          setToast('Feuille PDF téléchargée');
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Téléchargement impossible');
        } finally {
          setDownloadingPresencePdf(false);
        }
      };

      return (
        <div className="space-y-5">
          <AcademicPanelCard
            icon={<UserCheck className="h-5 w-5" />}
            title="Feuille de présences"
            subtitle="Sélectionnez la cohorte, la session et la date. Consultez les feuilles archivées ou en cours."
            flush
            footer={
              attendance?.apprenants?.length && !presenceSheetArchived ? (
                <button
                  type="button"
                  className={BTN_PRIMARY}
                  onClick={async () => {
                    try {
                      await saveAttendanceSheet(sessionId, {
                        dateSeance,
                        entries: attendance.apprenants.map((a) => ({
                          idInscription: a.idInscription,
                          statut: a.statut || 'PRESENT',
                        })),
                      });
                      setToast('Présences enregistrées');
                      await loadPresenceRegistry();
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'Échec enregistrement');
                    }
                  }}
                >
                  Enregistrer les présences
                </button>
              ) : presenceSheetArchived ? (
                <p className="text-sm font-medium text-on-surface-variant">Feuille archivée — lecture seule.</p>
              ) : null
            }
          >
            <div className="academic-panel-toolbar academic-panel-toolbar--spread">
              {renderCohorteSelect(true)}
              <label className="block w-full min-w-0 text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Session</span>
                <AcademiaSelect
                  className="mt-1 w-full"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                >
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.jourSemaine} · {s.vacation?.libelle} · {s.salle?.code}
                    </option>
                  ))}
                </AcademiaSelect>
              </label>
              <label className="block w-full min-w-0 text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Date de la séance</span>
                <input
                  type="date"
                  className={ACADEMIC_FIELD_INPUT}
                  value={dateSeance}
                  onChange={(e) => setDateSeance(e.target.value)}
                />
              </label>
              <div className="academic-panel-toolbar__action flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`${BTN_PRIMARY} w-full whitespace-nowrap sm:w-auto`}
                  onClick={() => void loadAttendance()}
                >
                  Charger
                </button>
                {canDownloadPresencePdf ? (
                  <button
                    type="button"
                    className={`${BTN_SECONDARY} inline-flex items-center gap-1.5`}
                    disabled={downloadingPresencePdf}
                    onClick={() => void handleDownloadPresencePdf()}
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    {downloadingPresencePdf ? 'Export…' : 'PDF'}
                  </button>
                ) : null}
              </div>
            </div>

            <div className="mb-4 flex flex-wrap gap-2 px-4 pt-2">
              <button
                type="button"
                className={`rounded-lg px-3 py-1.5 text-xs font-bold ${presenceListFilter === 'en_cours' ? 'bg-primary text-white' : 'border border-outline-variant/30'}`}
                onClick={() => setPresenceListFilter('en_cours')}
              >
                En cours ({enCoursRegistry.length})
              </button>
              <button
                type="button"
                className={`rounded-lg px-3 py-1.5 text-xs font-bold ${presenceListFilter === 'archivees' ? 'bg-primary text-white' : 'border border-outline-variant/30'}`}
                onClick={() => setPresenceListFilter('archivees')}
              >
                Archivées ({archivedRegistry.length})
              </button>
            </div>

            {presenceListFilter === 'archivees' ? (
              <div className="mx-4 mb-4 rounded-lg border border-outline-variant/20 bg-surface-container-low p-3">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                  Registre des feuilles archivées
                </p>
                {archivedRegistry.length === 0 ? (
                  <p className="text-sm text-on-surface-variant">Aucune feuille archivée pour cette session.</p>
                ) : (
                  <ul className="space-y-2">
                    {archivedRegistry.map((row) => {
                      const active = attendanceDayKey(row.dateSeance) === attendanceDayKey(dateSeance);
                      return (
                        <li key={row.dateSeance}>
                          <button
                            type="button"
                            className={`w-full rounded-md border px-3 py-2 text-left text-sm ${active ? 'border-primary bg-primary/5' : 'border-outline-variant/25'}`}
                            onClick={() => openRegistryDate(row.dateSeance)}
                          >
                            <span className="font-bold">{formatRegistryDate(row.dateSeance)}</span>
                            <span className="mt-0.5 block text-xs text-on-surface-variant">
                              {row.presencesCount} présence{row.presencesCount !== 1 ? 's' : ''}
                              {row.dateArchivage ? ` · archivée le ${formatRegistryDate(row.dateArchivage)}` : ''}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : enCoursRegistry.length > 0 ? (
              <div className="mx-4 mb-4 rounded-lg border border-outline-variant/20 bg-surface-container-low p-3">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                  Saisies en cours (non archivées)
                </p>
                <ul className="space-y-2">
                  {enCoursRegistry.map((row) => {
                    const active = attendanceDayKey(row.dateSeance) === attendanceDayKey(dateSeance);
                    return (
                      <li key={row.dateSeance}>
                        <button
                          type="button"
                          className={`w-full rounded-md border px-3 py-2 text-left text-sm ${active ? 'border-primary bg-primary/5' : 'border-outline-variant/25'}`}
                          onClick={() => openRegistryDate(row.dateSeance)}
                        >
                          <span className="font-bold">{formatRegistryDate(row.dateSeance)}</span>
                          <span className="mt-0.5 block text-xs text-on-surface-variant">
                            {row.presencesCount} présence{row.presencesCount !== 1 ? 's' : ''} · modifiable
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {presenceSheetArchived ? (
              <p className="mx-4 mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                Feuille archivée
                {attendance?.feuille?.dateArchivage
                  ? ` le ${formatRegistryDate(attendance.feuille.dateArchivage)}`
                  : ''}
                . Consultation seule — modification impossible.
              </p>
            ) : null}

            <AcademicPanelTableWrap
              empty={
                !attendance?.apprenants?.length
                  ? 'Aucun apprenant ou session non liée à une cohorte.'
                  : undefined
              }
            >
              <table className={TABLE_CLASS}>
                <thead>
                  <tr>
                    <th>Apprenant</th>
                    <th>Matricule</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance?.apprenants?.map((row) => (
                    <tr key={row.idInscription}>
                      <td>{row.nomComplet}</td>
                      <td className="font-mono text-xs">{row.matricule}</td>
                      <td>
                        <label className="block min-w-[8.5rem]">
                          <span className="sr-only">Statut de présence</span>
                          <AcademiaSelect
                            fieldSize="sm"
                            aria-label={`Statut de présence — ${row.nomComplet}`}
                            value={row.statut ?? 'PRESENT'}
                            disabled={presenceSheetArchived}
                            onChange={(e) => {
                              setAttendance((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      apprenants: prev.apprenants.map((a) =>
                                        a.idInscription === row.idInscription
                                          ? { ...a, statut: e.target.value }
                                          : a,
                                      ),
                                    }
                                  : prev,
                              );
                            }}
                          >
                            {PRESENCE_STATUTS.map((st) => (
                              <option key={st} value={st}>
                                {st}
                              </option>
                            ))}
                          </AcademiaSelect>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AcademicPanelTableWrap>
          </AcademicPanelCard>
        </div>
      );
    }

    if (tab === 'notes') {
      return (
        <div className="space-y-5">
          <AcademicPanelCard
            variant="accent"
            icon={<ClipboardList className="h-5 w-5" />}
            title="Ajouter une note"
            subtitle="Saisie des évaluations, TP, examens et soutenances pour la cohorte sélectionnée."
          >
            {noteCohorteSelect}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <label className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Apprenant</span>
                <AcademiaSelect
                  className="mt-1"
                  value={noteForm.idInscription}
                  onChange={(e) => setNoteForm((f) => ({ ...f, idInscription: e.target.value }))}
                >
                  <option value="">— Choisir —</option>
                  {notesRows.map((r) => (
                    <option key={r.idInscription} value={r.idInscription}>
                      {r.matricule} — {r.nomComplet}
                    </option>
                  ))}
                </AcademiaSelect>
              </label>
              <label className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Type d&apos;évaluation</span>
                <AcademiaSelect
                  className="mt-1"
                  value={noteForm.typeEvaluation}
                  onChange={(e) => setNoteForm((f) => ({ ...f, typeEvaluation: e.target.value }))}
                >
                  {EVAL_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </AcademiaSelect>
              </label>
              <label className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Libellé</span>
                <input
                  className={ACADEMIC_FIELD_INPUT}
                  value={noteForm.libelle}
                  onChange={(e) => setNoteForm((f) => ({ ...f, libelle: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Note (/20)</span>
                <input
                  type="number"
                  min={0}
                  max={20}
                  step={0.5}
                  className={ACADEMIC_FIELD_INPUT}
                  value={noteForm.noteSur20}
                  onChange={(e) => setNoteForm((f) => ({ ...f, noteSur20: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Coefficient</span>
                <input
                  type="number"
                  min={0.1}
                  step={0.1}
                  className={ACADEMIC_FIELD_INPUT}
                  value={noteForm.coefficient}
                  onChange={(e) => setNoteForm((f) => ({ ...f, coefficient: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className={ACADEMIC_FIELD_LABEL}>Créneau de soutenance</span>
                <input
                  type="datetime-local"
                  className={ACADEMIC_FIELD_INPUT}
                  value={noteForm.creneauSoutenance}
                  onChange={(e) => setNoteForm((f) => ({ ...f, creneauSoutenance: e.target.value }))}
                />
              </label>
              <label className="block text-sm sm:col-span-2 lg:col-span-3">
                <span className={ACADEMIC_FIELD_LABEL}>Fichier du dépôt (TP / soutenance)</span>
                <input
                  type="file"
                  className="academia-field mt-1 w-full text-sm text-slate-900 file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-primary"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.txt,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    setNoteDepotFile(file);
                  }}
                />
                {noteDepotFile ? (
                  <p className="mt-1.5 text-xs text-on-surface-variant">
                    Fichier sélectionné : <span className="font-semibold">{noteDepotFile.name}</span>
                  </p>
                ) : (
                  <p className="mt-1.5 text-xs text-on-surface-variant">
                    Optionnel — téléversé à l&apos;enregistrement.
                  </p>
                )}
              </label>
              <label className="block text-sm sm:col-span-2 lg:col-span-3">
                <span className={ACADEMIC_FIELD_LABEL}>Commentaire du jury</span>
                <textarea
                  rows={4}
                  className={`${ACADEMIC_FIELD_INPUT} academic-panel-field-textarea`}
                  placeholder="Observation, appréciation ou remarque du jury…"
                  value={noteForm.juryCommentaire}
                  onChange={(e) => setNoteForm((f) => ({ ...f, juryCommentaire: e.target.value }))}
                />
              </label>
              <div className="flex items-end sm:col-span-2 lg:col-span-3">
                <button
                  type="button"
                  className={BTN_PRIMARY}
                  disabled={savingNote}
                  onClick={async () => {
                    if (!noteForm.idInscription) return;
                    setSavingNote(true);
                    setError('');
                    try {
                      let urlFichier: string | undefined;
                      if (noteDepotFile) {
                        urlFichier = await uploadAdminFormateurFile(noteDepotFile);
                      }
                      await saveAcademicNote({
                        idInscription: noteForm.idInscription,
                        typeEvaluation: noteForm.typeEvaluation,
                        libelle: noteForm.libelle,
                        noteSur20: Number(noteForm.noteSur20),
                        coefficient: Number(noteForm.coefficient),
                        urlFichier,
                        juryCommentaire: noteForm.juryCommentaire || undefined,
                        creneauSoutenance: noteForm.creneauSoutenance || undefined,
                      });
                      setNoteDepotFile(null);
                      pushSuccessToast('Note enregistrée avec succès.');
                      await loadNotes();
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'Échec');
                    } finally {
                      setSavingNote(false);
                    }
                  }}
                >
                  {savingNote ? 'Enregistrement…' : 'Enregistrer la note'}
                </button>
              </div>
            </div>
          </AcademicPanelCard>
          <AcademicPanelCard
            icon={<ListOrdered className="h-5 w-5" />}
            title="Relevé des notes"
            subtitle={`${notesRows.length} apprenant(s) dans la cohorte`}
            badge={<span className="academic-panel-badge">{notesRows.length}</span>}
          >
            {notesRows.length === 0 ? (
              <p className="academic-panel-empty !py-8">Aucun apprenant dans cette cohorte.</p>
            ) : (
              <div className="space-y-3">
                {notesRows.map((row) => (
                  <article key={row.idInscription} className="academic-panel-note-card">
                    <header className="academic-panel-note-card__head">
                      <p className="academic-panel-note-card__name">
                        {row.nomComplet}
                        <span className="ml-2 font-mono text-xs font-semibold text-on-surface-variant">
                          {row.matricule}
                        </span>
                      </p>
                      {row.stats.moyenneSur20 != null ? (
                        <span className="academic-panel-badge academic-panel-badge--success">
                          Moy. {row.stats.moyenneSur20.toFixed(2)}/20
                        </span>
                      ) : (
                        <span className="academic-panel-badge academic-panel-badge--muted">Sans moyenne</span>
                      )}
                    </header>
                    <div className="academic-panel-note-card__body">
                      {row.notes.length === 0 ? (
                        <p className="text-sm text-on-surface-variant">Aucune note enregistrée.</p>
                      ) : (
                        row.notes.map((n) => (
                          <div key={n.id} className="academic-panel-note-row">
                            <span>
                              <span className="font-semibold text-slate-900">{n.typeLabel}</span>
                              {' — '}
                              {n.libelle}: <strong>{n.noteSur20}/20</strong>
                              {n.statutDepot ? (
                                <span className="ml-2 text-xs text-on-surface-variant">({n.statutDepot})</span>
                              ) : null}
                            </span>
                            <span className="flex flex-wrap items-center gap-2">
                              {n.typeEvaluation && PRATIQUE_TYPES.has(n.typeEvaluation) ? (
                                <label className="block min-w-[9rem]">
                                  <span className={ACADEMIC_FIELD_LABEL}>Statut du dépôt</span>
                                  <AcademiaSelect
                                    fieldSize="sm"
                                    aria-label="Statut du dépôt"
                                    value={n.statutDepot ?? 'DEPOSE'}
                                    onChange={async (e) => {
                                      await patchNoteStatutDepot(n.id, e.target.value);
                                      await loadNotes();
                                    }}
                                  >
                                    {STATUT_DEPOT_OPTIONS.map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </AcademiaSelect>
                                </label>
                              ) : null}
                              {canDelete ? (
                              <button
                                type="button"
                                className={`${BTN_DANGER} inline-flex items-center gap-1 !px-2.5 !py-1.5 text-xs`}
                                onClick={() =>
                                  setNoteToDelete({ id: n.id, libelle: `${n.typeLabel} — ${n.libelle}` })
                                }
                              >
                                <Trash2 className="h-3 w-3" aria-hidden />
                                Supprimer
                              </button>
                              ) : null}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </AcademicPanelCard>
        </div>
      );
    }

    if (tab === 'soutenances') {
      return (
        <AcademicPanelCard
          icon={<GraduationCap className="h-5 w-5" />}
          title="Planning des soutenances"
          subtitle="Planifiez les créneaux et suivez les dépôts des apprenants."
          flush
        >
          <div className="academic-panel-toolbar">{cohorteSelect}</div>
          <AcademicPanelTableWrap
            empty={
              soutenanceRows.length === 0 ? 'Aucun apprenant dans cette cohorte.' : undefined
            }
          >
            <table className={TABLE_CLASS}>
              <thead>
                <tr>
                  <th>Apprenant</th>
                  <th>Créneau</th>
                  <th>Dépôt</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {soutenanceRows.map((row) => (
                  <SoutenanceRowEditor
                    key={row.idInscription}
                    row={row}
                    onSave={async (creneauSoutenance) => {
                      await planifierSoutenanceAdmin(cohorteId, {
                        idInscription: row.idInscription,
                        creneauSoutenance,
                      });
                      setToast('Soutenance planifiée');
                      await loadSoutenances();
                    }}
                  />
                ))}
              </tbody>
            </table>
          </AcademicPanelTableWrap>
        </AcademicPanelCard>
      );
    }

    if (tab === 'evaluations') {
      return (
        <AcademicPanelCard
          icon={<Star className="h-5 w-5" />}
          title="Évaluations formateurs"
          subtitle="Retours des apprenants (ponctualité et pédagogie)."
          badge={
            evaluationsFormateur.length > 0 ? (
              <span className="academic-panel-badge">{evaluationsFormateur.length}</span>
            ) : null
          }
          flush
        >
          <div className="academic-panel-toolbar">{cohorteSelect}</div>
          <AcademicPanelTableWrap
            empty={
              evaluationsFormateur.length === 0 ? 'Aucune évaluation enregistrée.' : undefined
            }
          >
            <table className={TABLE_CLASS}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Formateur</th>
                  <th>Apprenant</th>
                  <th className="text-right">Notes</th>
                </tr>
              </thead>
              <tbody>
                {evaluationsFormateur.map((ev) => (
                  <tr key={ev.id}>
                    <td>{new Date(ev.dateCreation).toLocaleDateString('fr-FR')}</td>
                    <td>{ev.formateur.nom}</td>
                    <td>
                      {ev.apprenant.nom}
                      {ev.apprenant.matricule ? (
                        <span className="ml-1 font-mono text-xs text-on-surface-variant">
                          ({ev.apprenant.matricule})
                        </span>
                      ) : null}
                    </td>
                    <td className="text-right font-semibold">
                      P {ev.notePonctualite}/5 · Péd. {ev.notePedagogie}/5
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AcademicPanelTableWrap>
        </AcademicPanelCard>
      );
    }

    return (
      <div className="space-y-5">
        <AcademicPanelCard
          variant="accent"
          icon={<Trophy className="h-5 w-5" />}
          title="Délibération & classement"
          subtitle="Calculez le classement de la cohorte et consultez l'historique des PV."
          flush
        >
          <div className="academic-panel-toolbar academic-panel-toolbar--spread academic-panel-toolbar--deliberation">
            {renderCohorteSelect(true)}
            <label className="block w-full min-w-0 text-sm">
              <span className={ACADEMIC_FIELD_LABEL}>Période académique</span>
              <input
                className={ACADEMIC_FIELD_INPUT}
                value={periode}
                onChange={(e) => setPeriode(e.target.value)}
                placeholder="2026-S1"
              />
            </label>
            <div className="academic-panel-toolbar__actions">
              <button
                type="button"
                className={`${BTN_SECONDARY} w-full sm:w-auto`}
                onClick={async () => {
                  if (!cohorteId) return;
                  try {
                    const data = await previewCohorteClassement(cohorteId, periode);
                    setPreviewRows(data.classement ?? []);
                    setToast('Aperçu du classement (sans enregistrement)');
                  } catch (e) {
                    setError(e instanceof Error ? e.message : 'Échec aperçu');
                  }
                }}
              >
                Aperçu classement
              </button>
              <button
                type="button"
                className={`${BTN_PRIMARY} w-full sm:w-auto`}
                onClick={async () => {
                  setLoading(true);
                  setError('');
                  try {
                    const data = await runCohorteDeliberation(cohorteId, {
                      periode,
                      seuilAdmissionSur20: 10,
                    });
                    setDelibResult(data.resultats as Record<string, unknown>);
                    setToast('Délibération calculée — classement mis à jour');
                    await loadClassement();
                    await loadDeliberations();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : 'Échec délibération');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Lancer la délibération
              </button>
            </div>
          </div>
          {delibResult?.synthese ? (
            <div className="border-t border-outline-variant/20 px-5 py-4">
              <AcademicPanelStatBanner>
                {(delibResult.synthese as { total: number; admis: number }).total} apprenants ·{' '}
                {(delibResult.synthese as { admis: number }).admis} admis (seuil 10/20)
              </AcademicPanelStatBanner>
            </div>
          ) : null}
        </AcademicPanelCard>

        {previewRows.length > 0 ? (
          <AcademicPanelCard
            variant="preview"
            icon={<ListOrdered className="h-5 w-5" />}
            title="Aperçu du classement"
            subtitle="Simulation non enregistrée en base."
            badge={<span className="academic-panel-badge">Top 15</span>}
          >
            <ul className="max-h-52 space-y-0.5 overflow-y-auto">
              {previewRows.slice(0, 15).map((row) => (
                <li key={row.rang} className="academic-panel-preview-row">
                  <span>
                    <span className="font-bold text-primary">#{row.rang}</span> {row.nomComplet}
                    {row.admis ? (
                      <span className="academic-panel-badge academic-panel-badge--success ml-2">admis</span>
                    ) : (
                      <span className="academic-panel-badge academic-panel-badge--muted ml-2">
                        non admis
                      </span>
                    )}
                  </span>
                  <span className="font-semibold">{row.scoreGlobal}</span>
                </li>
              ))}
            </ul>
          </AcademicPanelCard>
        ) : null}

        <AcademicPanelCard
          icon={<History className="h-5 w-5" />}
          title="Historique des délibérations"
          subtitle="Procès-verbaux et sessions passées."
        >
          {deliberations.length === 0 ? (
            <p className="text-sm text-on-surface-variant">Aucune délibération enregistrée.</p>
          ) : (
            <ul className="academic-panel-list">
              {deliberations.map((d) => (
                <AcademicPanelListItem key={d.id}>
                  <span>
                    {new Date(d.dateDeliberation).toLocaleString('fr-FR')} —{' '}
                    <strong>{d.resultats?.periode ?? '—'}</strong> ({d.resultats?.synthese?.admis ?? 0}/
                    {d.resultats?.synthese?.total ?? 0} admis)
                  </span>
                  <button
                    type="button"
                    className={`${BTN_SECONDARY} !py-1.5 text-xs`}
                    onClick={() => void downloadDeliberationPdf(d.id)}
                  >
                    Télécharger PV (PDF)
                  </button>
                </AcademicPanelListItem>
              ))}
            </ul>
          )}
        </AcademicPanelCard>

        <AcademicPanelCard
          icon={<Trophy className="h-5 w-5" />}
          title="Classement officiel"
          subtitle={
            <>
              Publié sur{' '}
              <a href="/excellence" className="font-bold text-primary hover:underline" target="_blank" rel="noreferrer">
                /excellence
              </a>
            </>
          }
          badge={
            classement.length > 0 ? (
              <span className="academic-panel-badge">{classement.length} rangs</span>
            ) : null
          }
          flush
        >
          <AcademicPanelTableWrap
            empty={
              classement.length === 0
                ? 'Lancez une délibération pour générer le classement.'
                : undefined
            }
          >
            <table className={TABLE_CLASS}>
              <thead>
                <tr>
                  <th>Rang</th>
                  <th>Apprenant</th>
                  <th className="text-right">Score global</th>
                </tr>
              </thead>
              <tbody>
                {classement.map((row) => (
                  <tr key={row.rang}>
                    <td className="font-bold text-primary">#{row.rang}</td>
                    <td>{row.nomComplet}</td>
                    <td className="text-right font-semibold">{row.scoreGlobal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AcademicPanelTableWrap>
        </AcademicPanelCard>
      </div>
    );
  }, [
    tab,
    cohortes,
    cohorteId,
    sessions,
    sessionId,
    dateSeance,
    attendance,
    notesRows,
    vacations,
    classement,
    periode,
    delibResult,
    deliberations,
    academicConfig,
    vacationForm,
    noteForm,
    soutenanceRows,
    evaluationsFormateur,
    previewRows,
    vacationPagination,
  ]);

  return (
    <AdminPageShell
      title="Gestion académique"
      description="Présences, notes (TP, soutenances, examens), délibérations et classements intelligents."
      sideCard={{
        label: 'Vacations',
        value: String(vacations.length),
        helper: 'Créneaux horaires officiels',
        sideLinks: [
          { label: 'Planning', to: '/admin/planning', icon: 'calendar_month' },
          { label: 'Formations', to: '/admin/formations', icon: 'school' },
        ],
      }}
      isLoading={loading}
      loadingMessage="Chargement…"
    >
      <AdminToast message={toast || null} onClose={() => setToast('')} />
      {error ? (
        <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {error}
        </p>
      ) : null}
      <div className="academic-panel-tabs" role="tablist" aria-label="Sections gestion académique">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => {
              setTab(t.id);
              setToast('');
              setError('');
            }}
            className={`academic-panel-tab ${tab === t.id ? 'academic-panel-tab--active' : ''}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tabContent}

      <DeleteConfirmModal
        open={vacationToDelete !== null}
        title="Supprimer cette vacation ?"
        description={
          vacationToDelete
            ? `Cette action est irréversible. La vacation « ${vacationToDelete.libelle} » sera définitivement supprimée.`
            : 'Cette action est irréversible.'
        }
        isConfirming={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setVacationToDelete(null);
        }}
        onConfirm={async () => {
          if (!vacationToDelete || deleteBusy) return;
          setDeleteBusy(true);
          setError('');
          try {
            await deleteAcademicVacation(vacationToDelete.id);
            setVacations(await fetchAcademicVacations());
            setVacationToDelete(null);
            pushSuccessToast('Vacation supprimée avec succès.');
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Suppression impossible.');
          } finally {
            setDeleteBusy(false);
          }
        }}
      />

      <DeleteConfirmModal
        open={noteToDelete !== null}
        title="Supprimer cette note ?"
        description={
          noteToDelete
            ? `Cette action est irréversible. La note « ${noteToDelete.libelle} » sera définitivement supprimée.`
            : 'Cette action est irréversible.'
        }
        isConfirming={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setNoteToDelete(null);
        }}
        onConfirm={async () => {
          if (!noteToDelete || deleteBusy) return;
          setDeleteBusy(true);
          setError('');
          try {
            await deleteAcademicNote(noteToDelete.id);
            await loadNotes();
            setNoteToDelete(null);
            pushSuccessToast('Note supprimée avec succès.');
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Suppression impossible.');
          } finally {
            setDeleteBusy(false);
          }
        }}
      />
    </AdminPageShell>
  );
}
